/**
 * collectDiagnostics — 把「外部世界」餵給純推導層(R249 Step 1)
 * =============================================================================
 *
 * [diagnosticsReport](./diagnosticsReport.ts) 刻意不讀 store / 不碰 DOM,所有值由外面
 * 注入 —— 本檔就是那個注入邊界:讀 runtime config、`navigator`、`location`、以及兩個
 * store,組出 {@link DiagnosticsInput} 交給純層。
 *
 * 分成兩支的理由:payload 形狀是要單測的(遮罩對不對、欄位缺不缺),而「怎麼從
 * `window` 拿到視窗寬度」不值得測也測不出價值。
 */

import { config } from '@/config';
import { useActiveJobStore } from '@/stores/activeJobStore';
import { useGroupContextStore } from '@/stores/groupContextStore';

import { newClientRequestId, readApiTrail, readConsoleTrail } from './diagnosticsBuffer';
import {
  buildDiagnosticsReport,
  newReportId,
  type DiagnosticsActiveJob,
  type DiagnosticsReport,
  type ReportCategory,
} from './diagnosticsReport';

/**
 * 從 route 抽 project id。
 *
 * 🔴 **`/canvas/:projectId` 一定要認**(R257):畫布是最常出問題的一頁,而它的路由**不是**
 * `/projects/...` —— 少了這條,回報單的 `project_id` 在畫布頁永遠是 null,而那正是 BE 明說
 * 「後台修 bug 的定位線索」。實際回報單上抓到的(route 是 `/canvas/{id}` 但 projectId 為 null)。
 *
 * 兩條路由都收:`/canvas/:projectId`(畫布)與 `/projects/:pid/...`(專案子頁)。
 */
export function projectIdFromRoute(pathname: string): string | null {
  const m = /^\/(?:projects|canvas)\/([^/?#]+)/.exec(pathname);
  return m?.[1] ?? null;
}

/**
 * 進行中的 job 摘要。
 *
 * ⚠️ 只取**非終態**:終態的 job 留在 store 裡是為了讓使用者看得到結果(R228 清單「終態
 * 沉底」),但診斷要回答的是「現在有什麼正在跑」。已完成的 job 混進來會讓後端誤判成
 * 「使用者卡在生成中」。
 */
const TERMINAL_STATUSES = new Set(['SUCCEEDED', 'FAILED', 'CANCELLED']);

function collectActiveJobs(): DiagnosticsActiveJob[] {
  const out: DiagnosticsActiveJob[] = [];
  const byProject = useActiveJobStore.getState().activeJobs;
  for (const [projectId, jobs] of Object.entries(byProject)) {
    for (const [jobId, detail] of Object.entries(jobs)) {
      const job = detail?.job;
      if (!job) continue;
      if (TERMINAL_STATUSES.has(job.overallStatus)) continue;
      out.push({
        projectId,
        jobId,
        status: job.overallStatus,
        // 分母一律用 wire 的 `totalSteps`(含隱藏前置步)—— 自己數綠卡會少算,
        // BE 2026-08-01 已就此糾正過一次。拿不到就給 null,不編數字。
        progress:
          typeof job.totalSteps === 'number' && job.totalSteps > 0
            ? `${job.completedSteps}/${job.totalSteps}`
            : null,
      });
    }
  }
  return out;
}

/** 當下解析後的主題(`<html data-theme>`;R218 起一定有值)。 */
function readTheme(): string {
  if (typeof document === 'undefined') return '';
  return document.documentElement.dataset['theme'] ?? '';
}

export interface CollectDiagnosticsOptions {
  category: ReportCategory | null;
  note: string;
  /** 測試注入用;production 不傳。 */
  nowMs?: number;
  reportId?: string;
}

export function collectDiagnostics(opts: CollectDiagnosticsOptions): DiagnosticsReport {
  const pathname = typeof location === 'undefined' ? '' : location.pathname;
  const search = typeof location === 'undefined' ? '' : location.search;
  return buildDiagnosticsReport({
    reportId: opts.reportId ?? newReportId(newClientRequestId()),
    nowMs: opts.nowMs ?? Date.now(),
    category: opts.category,
    note: opts.note,
    appEnv: config.appEnv,
    // 空字串 = nginx 漏注入 APP_VERSION。誠實顯示成 `unknown` 而不是留白 ——
    // 後端看到 `unknown` 才知道要去查部署設定,留白會被當成「沒這個欄位」。
    buildVersion: config.appVersion || 'unknown',
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    language: typeof navigator === 'undefined' ? '' : navigator.language,
    theme: readTheme(),
    viewportWidth: typeof window === 'undefined' ? 0 : window.innerWidth,
    viewportHeight: typeof window === 'undefined' ? 0 : window.innerHeight,
    rawRoute: `${pathname}${search}`,
    groupId: useGroupContextStore.getState().currentGroupId,
    projectId: projectIdFromRoute(pathname),
    activeJobs: collectActiveJobs(),
    apiTrail: readApiTrail(),
    consoleTrail: readConsoleTrail(),
  });
}
